import { Fragment, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import ArrowIcon from "../Icons/ArrowIcon";
import CheckIcon from "../Icons/CheckIcon";
export interface option {
  id?: string;
  slug?: string;
  count?: number;
  name: string;
  code?: string;
  discount?: string | number | boolean;
}

interface SelectboxProps {
  title?: string;
  name?: string;
  value?: string;
  className?: string;
  selectOptions: option[];
  placeholder?: string;
  titleStyles?: string;
  setSelected?: React.Dispatch<React.SetStateAction<option | null>>;
  selected?: option | null;
  error?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
}

const Selectbox = ({
  title,
  className,
  selectOptions,
  name,
  value,
  placeholder,
  selected,
  setSelected,
  titleStyles,
  error,
  searchable = false,
  searchPlaceholder = "Search...",
}: SelectboxProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredOptions = searchable
    ? selectOptions.filter(
        (item) =>
          item.name.toLowerCase().includes(normalizedQuery) ||
          item.code?.toLowerCase().includes(normalizedQuery)
      )
    : selectOptions;

  return (
    <>
      <Listbox
        name={name}
        value={value || selected}
        onChange={(next: option) => {
          setSelected?.(next);
          setSearchQuery("");
        }}
      >
        <div className="relative w-full">
          {title && (
            <h1
              className={`mb-2 text-sm font-semibold text-textPrimary ${titleStyles}`}
            >
              {title}
            </h1>
          )}
          <Listbox.Button
            className={`relative block h-14 w-full rounded-xl border border-[#d8e0df] bg-[#f8faf9] px-4 py-3 text-left text-sm font-medium text-textPrimary transition-all duration-200 hover:border-secondary/50 focus:border-secondary focus:bg-white focus:outline-none focus:ring-4 focus:ring-secondary/10 ${error ? "border-red-600 focus:border-red-600 focus:ring-red-600/10" : ""} ${className}`}
          >
            <span className="block truncate">
              {selected ? selected.name : placeholder}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-lightGray">
              <ArrowIcon className="h-4 w-5 rotate-[270deg]" />
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-[9999] mt-2 max-h-60 w-full overflow-auto rounded-xl border border-black/10 bg-white p-1 text-base shadow-[0_18px_55px_rgba(17,24,22,0.16)] focus:outline-none sm:text-sm">
              {searchable && (
                <li
                  role="none"
                  className="sticky top-0 z-10 bg-white p-1.5"
                >
                  <input
                    autoFocus
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                    onKeyDown={(event) => {
                      if (event.key !== "Escape") event.stopPropagation();
                    }}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    className="h-10 w-full rounded-lg border border-black/10 bg-dashboardBg px-3 text-sm text-textPrimary outline-none transition focus:border-secondary focus:bg-white focus:ring-2 focus:ring-secondary/10"
                  />
                </li>
              )}
              {filteredOptions.map((option, optionIdx) => (
                <Listbox.Option
                  key={option.id || option.slug || optionIdx}
                  className={({ active }) =>
                    `relative cursor-default select-none rounded-lg py-2.5 pl-10 pr-4 text-textPrimary ${
                      active ? "bg-secondary/10 text-primary" : ""
                    }`
                  }
                  value={option}
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block text-sm truncate ${
                          selected ? "font-medium" : "font-normal"
                        }`}
                      >
                        {option.name}
                      </span>
                      {selected ? (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-secondary">
                          <CheckIcon />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
              {searchable && filteredOptions.length === 0 && (
                <li
                  role="none"
                  className="px-3 py-4 text-center text-sm font-medium text-lightGray"
                >
                  No coupons found
                </li>
              )}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
      {error && (
        <span className="text-xs font-semibold text-red-600 sm:text-sm">
          {error}
        </span>
      )}
    </>
  );
};

export default Selectbox;
